import React, { useEffect, useRef, useState } from "react";
import { Spinner, Flex, Box, Label, Heading } from "@theme-ui/components";
import _ from "lodash";
import { navigate } from "gatsby";
import { colors, typography, Button, InputBox, Dropdown } from "@greythr/shared-components";
import { saveForm, validate } from "@greythr/shared-utils/hsAPI";
import api from "@greythr/shared-utils/api";
import { getRegionFromSFPicklist } from "@greythr/shared-utils/region-selector-salesforce";
import { pspFormValuesMap } from "./form-utils";
import { EmailConsentCheckbox } from "@greythr/shared-components";
import ErrorComponent from "../customer-upsell/ErrorComponent";

let payload = {};
let captcha = "";
let userDetails = {};
let instanceID = "";

const Input = ({
  label,
  name,
  value,
  maxlength,
  error,
  onChange,
  eventHandlerText,
  eventHandler,
  ...props
}) => (
  <InputBox
    label={label}
    isRequired={true}
    labelColor={colors.black}
    errorColor={colors.errorMain}
    name={name}
    value={value}
    maxlength={maxlength}
    customStyles={{
      width: "100%",
      mb: "0.75rem",
      pb: 0,
    }}
    error={error}
    onChange={onChange}
    eventHandlerText={eventHandlerText}
    eventHandler={eventHandler}
    {...props}
  />
);

const industryData = [
  "Payroll Processor",
  "CA",
  "CS",
  "HR Consultant",
  "Compliance Consultant",
  "Others",
].map((item) => ({
  label: item,
  value: item,
}));

const PSPForm = ({
  recaptchaRef,
  regionCode,
  resetFormVar,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [validOtp, setValidOtp] = useState(false);
  const [showOtpResendLink, setShowOtpResendLink] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState("");
  const [message, setMessage] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const [error, setError] = useState({});
  const [processing, setProcessing] = useState(false);

  const [apiFailure, setApiFailure] = useState(false);
  const [userConsentChecked, setUserConsentChecked] = useState(true);
  let btnRef = useRef();

  useEffect(() => {
    if (otpSent) {
      const resendOtpTimerInterval = setInterval(
        () => setShowOtpResendLink(true),
        8000
      );
      return () => {
        if (resendOtpTimerInterval) {
          clearInterval(resendOtpTimerInterval);
        }
      };
    }
  }, [otpSent]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const companyFromParam = urlParams.get("company");
    if (companyFromParam) {
      sessionStorage.setItem("company", companyFromParam);
    }
  }, []);
  
  const phoneNoLength = 12;
  const handleOnChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setPhoneNumber(value);
    if (value.length > 0 && value.length < 10) {
      setPhoneError("Phone number must be at least 10 digits");
    } else {
      setPhoneError("");
    }
  };

  const validateForm = () => {
    let error = {};
    if (email) {
      const emailErr = validate(email);
      emailErr && (error["email"] = emailErr);
    }

    return error;
  };

  const requestOtp = () => {
    setOtp("");
    setShowOtpResendLink(false);
    setOtpErrorMessage("");

    if (email) {
      api
        .post("/generate_and_send_email_otp/", {
          email: email,
          is_trial: false,
          name: firstName + " " + lastName,
        })
        .then((response) => {
          const { sent } = response.data;
          if (!sent) {
            setProcessing(false);
            setMessage("Please enter a valid Email Address.");
          } else {
            setProcessing(false);
            setMessage("");
            setOtpSent(sent);
          }
        })
        .catch((error) => {
          console.log("Otp request failed: ", error);
          setProcessing(false);
          if (error?.response?.status == 400) {
            setMessage("Please enter a valid Email Address.");
            return;
          }
          setMessage("Network Error. Please try again!");
        });
    } else {
      setProcessing(false);
      setMessage("Please enter Email Address.");
    }
  };

  const verfiyOtp = async (payload) => {
    recaptchaRef.current.reset();
    captcha = await recaptchaRef.current.executeAsync();

    const newPayload = {
      ...payload,
      "Email Verification": "Yes",
      "Privacy Policy": userConsentChecked ? "True" : null,
      invisible_recaptcha: true,
      captcha,
    };
    if (otp) {
      api
        .post("/verify_email_otp/", { email, otp })
        .then((response) => {
          const { is_valid } = response.data;
          if (is_valid) {
            setValidOtp(true);
            setOtpErrorMessage("");
            saveForm(
              newPayload,
              (response) => {
                setProcessing(false);
                sessionStorage.setItem(
                  "userDetails",
                  JSON.stringify(userDetails)
                );
                navigate("/psp-thank-you/");
              },
              (error) => {
                setProcessing(false);
                if (
                  error.response &&
                  error.response.status == 400 &&
                  error.response.data.invalid_email
                ) {
                  setError((prev) => ({
                    ...prev,
                    email: error.response.data.invalid_email,
                  }));
                } else {
                  setApiFailure(true);
                }
              }
            );
          } else {
            setValidOtp(false);
            setOtpErrorMessage("Invalid OTP!");
          }
        })
        .catch((error) => {
          console.log(error);
          setApiFailure(true);
        });
    } else {
      setOtpErrorMessage("Please enter OTP");
    }
  };

  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setUserConsentChecked(isChecked);
  };

  const handleClick = async (e) => {
    setError({});
    setProcessing(true);

    const formErrors = validateForm();

    if (!_.isEmpty(formErrors)) {
      setError(formErrors);
      setProcessing(false);
      return;
    }
    const urlParams = new URLSearchParams(window?.location?.search);
    const acFormTag = urlParams?.get("ac_form_tag");
    const utm_source = urlParams?.get("utm_source");
    const utm_campaign = urlParams?.get("utm_campaign");
    const utm_medium = urlParams?.get("utm_medium");
    const nameCapture = urlParams?.get("nc");
    const driverId = urlParams?.get("d");
    const regionFromParam = urlParams?.get("Region");
    const source = urlParams?.get("Source");
    const subSourceFromParam = urlParams?.get("Sub_Source");

    const regionFromSFPicklist = await getRegionFromSFPicklist();

    if (recaptchaRef && recaptchaRef.current) {
      recaptchaRef.current.reset();
      captcha = await recaptchaRef.current.executeAsync();

      instanceID = crypto?.randomUUID();

      payload = {
        "Instance Id": instanceID,
        Email: email,
        "Form Id": "WF_PSP_CONTACT_US",
        "Phone Number": phoneNumber,
        "Email Verification": "No",
        "First Name": firstName,
        "Last Name": lastName,
        "Company Name": companyName,
        "Industry": industry,
        "Lp Form Tag": acFormTag,
        "Page Code": "PSP_CONTACT_US",
        "Page Name": pspFormValuesMap[regionCode]["Page Name"],
        "Page Url": `${window?.location?.origin}${window?.location?.pathname}`,
        "Form Code": pspFormValuesMap[regionCode]["Form Code"],
        Region: regionFromParam || regionFromSFPicklist,
        "Privacy Policy": userConsentChecked ? "True" : null,
        invisible_recaptcha: true,
        captcha,
      };

      if (nameCapture) payload["nc"] = nameCapture;
      if (driverId) payload["d"] = driverId;
      if (source) payload["Source"] = source;
      if (subSourceFromParam) payload["Sub Source"] = subSourceFromParam;
      if (utm_source) payload["Utm Source"] = utm_source;
      if (utm_campaign) payload["Utm Campaign"] = utm_campaign;
      if (utm_medium) payload["Utm Medium"] = utm_medium;
      if (window?.location?.href) payload["Utm Url"] = window.location.href;

      userDetails = {
        name: firstName + " " + lastName,
        email: email,
        phone: phoneNumber,
        companyName: companyName,
      };

      if (captcha) {
        requestOtp();
        saveForm(
          payload,
          (response) => {
            setApiFailure(false);
          },
          (error) => {
            setApiFailure(true);
          }
        );
      } else {
        setProcessing(false);
        setApiFailure(true);
      }
    } else {
      setProcessing(false);
      setApiFailure(true);
    }
  };

  const resetForm = () => {
    setApiFailure(false);
    setValidOtp(false);
    setOtpSent(false);
    setShowOtpResendLink(false);
    setOtp("");
    setOtpErrorMessage(false);
    setEmail("");
    setError({});
    setProcessing(false);
    setPhoneNumber("");
    setFirstName("");
    setLastName("");
    setMessage("");
    setUserConsentChecked(true);
  };

  useEffect(() => {
    if (resetFormVar) {
      resetForm();
    }
  }, [resetFormVar]);

  if (apiFailure)
    return (
      <ErrorComponent
        setValidOtp={setValidOtp}
        setApiFailure={setApiFailure}
        resetForm={resetForm}
        regionCode={regionCode}
      />
    );

  return (
    <Flex sx={mainContainerStyles}>
      <Box>
        <Box as="form">
          <Heading sx={{...typography.h6, mb: "2rem" }}>Talk to us</Heading>
          <Box sx={formStyles}>
            <Input
              type="text"
              label="First Name"
              value={firstName}
              name="first Name"
              maxlength={100}
              error={error?.name}
              disabled={otpSent}
              onChange={(e) => {
                const value = e.target.value;
                if (value.trim() === "") {
                  setFirstName("");
                  return;
                }
                setFirstName(value);
              }}
            />
            <Input
              type="text"
              label="Last Name"
              value={lastName}
              name="Last Name"
              maxlength={100}
              error={error?.name}
              disabled={otpSent}
              onChange={(e) => {
                const value = e.target.value;
                if (value.trim() === "") {
                  setLastName("");
                  return;
                }
                setLastName(value);
              }}
            />
            <Input
              type="text"
              label="Company Name"
              value={companyName}
              name="Company Name"
              maxlength={100}
              error={error?.companyName}
              disabled={otpSent}
              onChange={(e) => {
                const value = e.target.value;
                if (value.trim() === "") {
                  setCompanyName("");
                  return;
                }
                setCompanyName(value);
              }}
            />
            <Box>
              <Label sx={{ ...typography.textMd400 }}>
                 Industry&nbsp;
                <Box sx={{ color: colors.errorMain }}>*</Box>
              </Label>
              <Dropdown
                options={industryData}
                placeholder="Select"
                menuPlacement="bottom"
                isDisabled={otpSent}
                value={industryData.find(
                  (option) => option.value === industry,
                )}
                onChange={(selectedOption) =>
                  setIndustry(selectedOption.value)
                }
              />
            </Box>
            <Box>
              <Input
                label="Business Email"
                name="email"
                placeholder="For OTP verification..."
                value={email}
                maxlength={100}
                error={error?.email || message}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  if (newEmail.trim() === "") {
                    setEmail("");
                    return;
                  }
                  setEmail(newEmail);
                  const emailErr = validate(newEmail);
                  if (!emailErr || !newEmail) {
                    setMessage("");
                    setError((prev) => ({ ...prev, email: "" }));
                  }
                }}
                disabled={otpSent}
                eventHandlerText={otpSent && "Change?"}
                eventHandler={() => {
                  setEmail(""),
                    setOtpSent(false),
                    setMessage(""),
                    setError((prev) => ({ ...prev, email: "" }));
                }}
                customStyles={{
                  mb: "0.5rem",
                  paddingBottom: 0,
                }}
              />
              {otpSent && (
                <Box
                  sx={{
                    mb: "0.75rem",
                    ...typography.textSm,
                  }}
                >
                  *An OTP has been sent to your Email.
                </Box>
              )}
            </Box>
            <Input
              type="text"
              label="Phone Number"
              value={phoneNumber}
              name="phoneNumber"
              maxlength={phoneNoLength}
              minlength={10}
              error={phoneError}
              disabled={otpSent}
              onChange={handleOnChange}
            />
            {otpSent && (
              <Box>
                <InputBox
                  type="text"
                  value={otp}
                  label="OTP"
                  isRequired={true}
                  labelColor={colors.black}
                  placeholder="Type here..."
                  name="otp"
                  error={otpErrorMessage}
                  onChange={(e) => {
                    setMessage("");
                    const otp = e.target.value;
                    setOtp(otp);
                    if (!otp) setOtpErrorMessage("");
                  }}
                  customStyles={{ paddingBottom: 0 }}
                  eventHandlerText={showOtpResendLink && "Resend OTP"}
                  eventHandler={() => requestOtp()}
                />
              </Box>
            )}
          </Box>
          <EmailConsentCheckbox
            checked={userConsentChecked}
            onChange={handleCheckboxChange}
            customText="I agree to receive email updates, promotions, and personalized offers from greytHR. I understand I can unsubscribe or manage my preferences through the link in any email."
            customStyles={{
              mt: "0.75rem",
            }}
          />
        </Box>
      </Box>
      <Flex sx={buttonSectionStyles}>
        {processing ? (
          <Flex
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Spinner color={colors.primaryMain} size={48} />
          </Flex>
        ) : !otpSent ? (
          <Button
            id="psp_get_otp_request_btn"
            variant="filled"
            size="large"
            disabled={
              !email ||
              !firstName ||
              !lastName ||
              !companyName ||
              !industry ||
              !phoneNumber ||
              phoneError
            }
            onClick={handleClick}
          >
            Submit
          </Button>
        ) : (
          <Button
            id="psp_get_otp_verify_btn"
            variant="filled"
            size="large"
            disabled={!otp || processing}
            onClick={() => verfiyOtp(payload)}
            ref={btnRef}
          >
            Verify
          </Button>
        )}
      </Flex>
    </Flex>
  )
};

const { mainContainerStyles, formStyles, buttonSectionStyles } =
  {
    mainContainerStyles: {
      flexDirection: "column",
      justifyContent: "space-between",
      margin: "auto",
      mx: ["1rem", "1rem", "2rem", "auto"],
    },
    formStyles: {
      display: "grid",
      gridTemplateColumns: ["1fr", "1fr", "1fr 1fr"],
      columnGap: "1rem",
      rowGap: "1rem",
    },
    buttonSectionStyles: {
      mt: ["2rem", "2rem", "3rem", "4rem"],
      justifyContent: "center",
      alignItems: "center",
    },
  };
export default PSPForm;